import { create } from 'zustand';
import { createLogger } from '../utils/logger';

const log = createLogger('MapStore');

// Boundary data interface for background loading
export interface BoundaryData {
  type: 'country' | 'state' | 'city';
  name: string;
  geometry: any; // GeoJSON geometry
}

export interface MapStoreState {
  center: { lat: number; lon: number } | null;
  zoom: number;
  selectedStationCode: string | null;
  tooltipOpen: boolean;
  highlightBordersMode: boolean;
  boundaries: BoundaryData[];
  boundariesLoading: boolean;
}

export interface MapStoreActions {
  setMapCenter: (center: { lat: number; lon: number } | null, zoom?: number) => void;
  setZoom: (zoom: number) => void;
  selectStation: (stationCode: string | null, center?: { lat: number; lon: number } | null, zoom?: number) => void;
  toggleHighlightBorders: () => void;
  setHighlightBordersMode: (enabled: boolean) => void;
  setTooltipOpen: (open: boolean) => void;
  setBoundaries: (boundaries: BoundaryData[]) => void;
  setBoundariesLoading: (loading: boolean) => void;
}

export type MapStore = MapStoreState & MapStoreActions;

// Persist highlight borders preference
const loadHighlightBordersModePreference = (): boolean => {
  try {
    const saved = localStorage.getItem('highlightBordersMode');
    if (saved !== null) return JSON.parse(saved);
  } catch {
    // ponytail: localStorage may throw in private browsing; fallback to default
  }
  return true;
};

const persistHighlightBordersMode = (enabled: boolean) => {
  try {
    localStorage.setItem('highlightBordersMode', JSON.stringify(enabled));
  } catch {
    // ponytail: best-effort persistence
  }
};

export const useMapStore = create<MapStore>((set, get) => ({
  center: null,
  zoom: 10,
  selectedStationCode: null,
  tooltipOpen: false,
  highlightBordersMode: loadHighlightBordersModePreference(),
  boundaries: [],
  boundariesLoading: false,

  setMapCenter: (center, zoom) => {
    const updates: Partial<MapStoreState> = { center };
    if (zoom !== undefined) updates.zoom = zoom;
    set(updates);
  },

  setZoom: (zoom) => set({ zoom }),

  selectStation: (stationCode, center, zoom) => {
    const updates: Partial<MapStoreState> = {
      selectedStationCode: stationCode,
      tooltipOpen: stationCode !== null,
    };
    if (center !== undefined) updates.center = center;
    if (zoom !== undefined) updates.zoom = zoom;
    set(updates);
  },

  toggleHighlightBorders: () => {
    const next = !get().highlightBordersMode;
    set({ highlightBordersMode: next });
    persistHighlightBordersMode(next);
    log.debug(`Highlight borders mode: ${next}`);
  },

  setHighlightBordersMode: (enabled) => {
    set({ highlightBordersMode: enabled });
    persistHighlightBordersMode(enabled);
  },

  setTooltipOpen: (open) => set({ tooltipOpen: open }),

  setBoundaries: (boundaries) => set({ boundaries, boundariesLoading: false }),

  setBoundariesLoading: (loading) => set({ boundariesLoading: loading }),
}));
